import React, { useState, useEffect } from "react";
import FormPage from "../../shared/Layouts/FormPage";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getInvoice } from "../../redux/actions/invoiceAction";
import axios from "axios";
import { BASE_URL } from "../../constants";
import { isAuthenticated } from "../../services/auth";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Serve the worker from our own origin. A cross-origin worker URL is blocked
// by the browser, and the version on a CDN drifts from the pdfjs-dist that
// react-pdf actually pins — either one surfaces as "Failed to load PDF file."
// scripts/copy-pdf-worker.js keeps public/pdf.worker.min.mjs in sync.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const ViewInvoice = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const [data, setData] = useState({});
    const [pdfData, setPdfData] = useState(null);
    const [numPages, setNumPages] = useState();
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfLoaded, setPdfLoaded] = useState(false);
    const [loadError, setLoadError] = useState("");

    useEffect(() => {
        if (!id) return;
        let cancelled = false;

        dispatch(
            getInvoice(id, async (invoice) => {
                if (cancelled) return;
                setData(invoice);

                // pdfPath: the API streams the PDF itself (current). fileUrl: an
                // older API wrote the file to disk and returned a /public link.
                // Accept both so the panel and the API can deploy in any order.
                const streamed = Boolean(invoice?.pdfPath);
                const url = streamed
                    ? `${BASE_URL}${invoice.pdfPath}`
                    : invoice?.fileUrl;

                if (!url) {
                    setLoadError(
                        "The server returned no invoice location. If the API was just updated, rebuild and redeploy the admin panel."
                    );
                    return;
                }

                // Fetching the bytes here rather than handing pdf.js a URL means
                // a failure reports the real status instead of the generic
                // "Failed to load PDF file."
                try {
                    const res = await axios.get(url, {
                        responseType: "arraybuffer",
                        // The streaming endpoint is authenticated; the legacy
                        // /public link is not.
                        headers: streamed
                            ? { Authorization: `Bearer ${isAuthenticated()}` }
                            : undefined,
                    });
                    if (!cancelled) setPdfData({ data: new Uint8Array(res.data) });
                } catch (err) {
                    if (cancelled) return;
                    const status = err?.response?.status;
                    setLoadError(
                        status
                            ? `Could not download the invoice (HTTP ${status}) from ${url}`
                            : `Could not reach the invoice at ${url}. ${err.message}`
                    );
                }
            })
        );

        return () => {
            cancelled = true;
        };
    }, [id, dispatch]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPageNumber(1);
        setPdfLoaded(true);
        setLoadError("");
    };

    const onDocumentLoadError = (error) => {
        console.error("PDF render failed:", error);
        setPdfLoaded(false);
        setLoadError(`The file downloaded but could not be rendered. ${error?.message || ""}`);
    };

    const buttonStyle = {
        width: "120px",
        padding: "10px 15px",
        margin: "8px 0",
        backgroundColor: "#FFC107",
        color: "white",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontWeight: "bold",
        fontSize: "14px",
    };

    const handleDownload = () => {
        const blob = new Blob([pdfData.data], { type: "application/pdf" });
        const href = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = href;
        link.download = `${data.invoiceId || "invoice"}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
    };

    return (
        <FormPage>
            <div style={{ display: "flex", alignItems: "start", padding: "20px", gap: "40px" }}>
                <div>
                    {loadError && (
                        <div style={{ color: "#b00020", maxWidth: "800px", lineHeight: 1.5 }}>
                            {loadError}
                        </div>
                    )}
                    {!loadError && !pdfData && <p>Loading invoice...</p>}
                    {pdfData && (
                        <Document
                            file={pdfData}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            loading={<p>Rendering PDF...</p>}
                        >
                            <Page pageNumber={pageNumber} width={800} />
                        </Document>
                    )}
                    {pdfLoaded && numPages > 1 && (
                        <div style={{ marginTop: "10px" }}>
                            <button onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={pageNumber <= 1}>
                                Previous
                            </button>
                            <span style={{ margin: "0 10px" }}>
                                Page {pageNumber} of {numPages}
                            </span>
                            <button onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))} disabled={pageNumber >= numPages}>
                                Next
                            </button>
                        </div>
                    )}
                </div>
                {pdfLoaded && (
                    <div
                        className="shadow-1"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            background: "#ffff",
                            borderRadius: "10px",
                            padding: "10px",
                            top: "100px",
                        }}
                    >
                        <button style={buttonStyle} onClick={handleDownload}>
                            Download
                        </button>
                    </div>
                )}
            </div>
        </FormPage>
    );
};

export default ViewInvoice;
